import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Modal } from '@/components/ui/Modal';
import { cn } from '@/lib/utils';

export type PickerOption = { value: string; label: string };

/**
 * Фильтр-фишка сотрудника на телефоне (13.10.2026, docs/CURATOR-MOBILE-DESIGN.md §3, правило 7): в ряд фишек — статус,
 * курс, куратор; нажатие открывает окно выбора во весь экран с поиском. Выпадающий список на 390 px с сотней курсов
 * не листается по-человечески, а названия курсов в нём обрезаются.
 *
 * Пустое значение (`''`) — «все»: фишка тогда серая и подписана `label`; выбранное — оранжевая с названием выбора.
 */
export function MPickerChip({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  /** Первая строка с `value: ''` — «все» (как у прежних выпадающих списков). */
  options: PickerOption[];
  onChange: (value: string) => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const current = options.find((o) => o.value === value);
  const active = value !== '';
  const shown = useMemo(() => {
    const needle = q.trim().toLocaleLowerCase();
    return needle ? options.filter((o) => o.label.toLocaleLowerCase().includes(needle)) : options;
  }, [options, q]);

  return (
    <>
      <button
        type="button"
        data-picker-chip
        onClick={() => setOpen(true)}
        className={cn(
          'flex min-h-11 max-w-[70vw] flex-none items-center gap-1 rounded-full px-3.5 text-[12.5px] font-semibold',
          active ? 'bg-primary text-on-primary' : 'bg-surface-container-low text-on-surface',
        )}
      >
        <span className="truncate">{active && current ? current.label : label}</span>
        <span className="material-symbols-outlined text-[16px]" aria-hidden>
          expand_more
        </span>
      </button>
      <Modal
        open={open}
        onClose={() => {
          setOpen(false);
          setQ('');
        }}
        title={label}
        phoneFull
      >
        {/* Рамка фокуса — у круглой обёртки: общее `*:focus-visible` рисовало прямоугольник внутри круглого поля. */}
        <label className="mb-2 flex items-center gap-2 rounded-full bg-surface-container-low px-4 focus-within:ring-2 focus-within:ring-primary">
          <span className="material-symbols-outlined text-[18px] text-on-surface-variant">search</span>
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t('common.search')}
            className="h-11 w-full bg-transparent text-[14px] text-on-surface outline-none focus-visible:outline-none placeholder:text-on-surface-variant"
          />
        </label>
        <ul data-picker-list>
          {shown.map((o) => (
            <li key={o.value || '__all'}>
              <button
                type="button"
                onClick={() => {
                  onChange(o.value);
                  setOpen(false);
                  setQ('');
                }}
                className="flex min-h-11 w-full items-center gap-3 border-b border-outline py-2 text-left text-[14px] text-on-surface"
              >
                <span className="min-w-0 flex-1">{o.label}</span>
                {o.value === value && (
                  <span className="material-symbols-outlined text-[20px] text-primary" aria-hidden>
                    check
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>
      </Modal>
    </>
  );
}
